import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, LogOut, Search, Loader2, Package, ExternalLink, Upload, X,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRANDS, formatPrice } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = ["Smartphone", "Tablet", "Laptop", "Desktop", "Smartwatch", "Audio", "Kamera", "VR", "Aksesori", "TV & Media"];

const EMPTY = {
  name: "", brand: "Apple", category: "Smartphone", variant: "", description: "",
  specs: "", price: "", images: "", in_stock: true, featured: false, bestseller: false,
};

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/products", { params: q ? { q } : {} })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [q]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/admin/login"); return; }
    load();
  }, [authLoading, user, load, navigate]);

  if (authLoading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0C10]">
        <Loader2 className="h-8 w-8 animate-spin text-berry" />
      </div>
    );
  }

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, brand: p.brand, category: p.category, variant: p.variant || "",
      description: p.description || "", specs: (p.specs || []).join("\n"),
      price: p.price ?? "",
      images: (p.images && p.images.length ? p.images : p.image ? [p.image] : []).join("\n"),
      in_stock: p.in_stock, featured: p.featured, bestseller: p.bestseller,
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const imgs = form.images.split("\n").map((s) => s.trim()).filter(Boolean);
    const payload = {
      name: form.name, brand: form.brand, category: form.category, variant: form.variant,
      description: form.description,
      specs: form.specs.split("\n").map((s) => s.trim()).filter(Boolean),
      price: form.price === "" || form.price === null ? null : parseInt(form.price, 10),
      image: imgs[0] || "", images: imgs,
      in_stock: form.in_stock, featured: form.featured, bestseller: form.bestseller,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.slug}`, payload);
        toast.success("Produk diperbarui");
      } else {
        await api.post("/products", payload);
        toast.success("Produk ditambahkan");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan produk");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Gagal mengunggah ${file.name}`);
      }
    }
    if (uploaded.length) {
      setForm((f) => {
        const current = f.images.split("\n").map((s) => s.trim()).filter(Boolean);
        return { ...f, images: [...current, ...uploaded].join("\n") };
      });
      toast.success(`${uploaded.length} gambar diunggah`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const imageList = () => form.images.split("\n").map((s) => s.trim()).filter(Boolean);
  const removeImage = (idx) => {
    const list = imageList().filter((_, i) => i !== idx);
    setForm((f) => ({ ...f, images: list.join("\n") }));
  };


  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/products/${deleteTarget.slug}`);
      toast.success("Produk dihapus");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error("Gagal menghapus produk");
    }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-[#0B0C10]">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0B0C10]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3.5 sm:px-6">
          <img src="/assets/logo.png" alt="BigBerry" className="h-9 w-9 rounded-lg" />
          <div>
            <h1 className="font-heading text-base font-700 leading-none text-white">Admin Dashboard</h1>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <a href="/" target="_blank" rel="noopener noreferrer" className="hidden items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-sm text-gray-300 hover:text-white sm:inline-flex">
              <ExternalLink className="h-4 w-4" /> Lihat Situs
            </a>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-sm text-gray-300 hover:border-red-500/50 hover:text-red-400">
              <LogOut className="h-4 w-4" /> Keluar
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-berry" />
            <h2 className="font-heading text-2xl font-700 text-white">Produk <span className="text-gray-500">({products.length})</span></h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk..." data-testid="admin-search" className="w-56 border-white/10 bg-white/5 pl-9 text-white" />
            </div>
            <button onClick={openCreate} data-testid="admin-add-product" className="inline-flex items-center gap-2 rounded-full bg-berry px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-berry-dark">
              <Plus className="h-4 w-4" /> Tambah
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-white/5 bg-[#12141C]">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-berry" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3 font-medium">Produk</th>
                    <th className="px-4 py-3 font-medium">Brand</th>
                    <th className="px-4 py-3 font-medium">Kategori</th>
                    <th className="px-4 py-3 font-medium">Harga</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 text-right font-medium">Aksi</th>
                  </tr>
                </thead>
                <tbody data-testid="admin-product-table">
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img src={p.image || "/assets/logo.png"} alt="" className="h-10 w-10 rounded-lg bg-[#0B0C10] object-contain p-1" />
                          <div>
                            <p className="font-medium text-white">{p.name}</p>
                            {p.variant && <p className="text-xs text-gray-500">{p.variant}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{p.brand}</td>
                      <td className="px-4 py-3 text-gray-400">{p.category}</td>
                      <td className="px-4 py-3 text-gray-300">{formatPrice(p.price) || <span className="text-gray-500">—</span>}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${p.in_stock ? "bg-whatsapp/20 text-whatsapp" : "bg-white/10 text-gray-400"}`}>{p.in_stock ? "Ready" : "Habis"}</span>
                          {p.featured && <span className="rounded-full bg-berry/20 px-2 py-0.5 text-[10px] font-medium text-berry">Unggulan</span>}
                          {p.bestseller && <span className="rounded-full bg-electric/20 px-2 py-0.5 text-[10px] font-medium text-blue-400">Best</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.slug}`} className="rounded-lg border border-white/10 p-2 text-gray-300 hover:border-electric hover:text-electric"><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => setDeleteTarget(p)} data-testid={`admin-delete-${p.slug}`} className="rounded-lg border border-white/10 p-2 text-gray-300 hover:border-red-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#12141C] text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Edit Produk" : "Tambah Produk"}</DialogTitle>
            <DialogDescription className="text-gray-500">
              Isi detail produk, harga, dan unggah foto. Foto pertama menjadi gambar utama.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label className="text-gray-300">Nama Produk</Label>
              <Input required value={form.name} onChange={(e) => set("name")(e.target.value)} data-testid="form-name" className="mt-1.5 border-white/10 bg-white/5 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Brand</Label>
                <Select value={form.brand} onValueChange={set("brand")}>
                  <SelectTrigger data-testid="form-brand" className="mt-1.5 border-white/10 bg-white/5 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#1A1D24] text-white">
                    {BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Kategori</Label>
                <Select value={form.category} onValueChange={set("category")}>
                  <SelectTrigger data-testid="form-category" className="mt-1.5 border-white/10 bg-white/5 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#1A1D24] text-white">
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Varian (kapasitas/warna)</Label>
              <Input value={form.variant} onChange={(e) => set("variant")(e.target.value)} data-testid="form-variant" placeholder="cth: 256GB, Titanium" className="mt-1.5 border-white/10 bg-white/5 text-white" />
            </div>
            <div>
              <Label className="text-gray-300">Harga (Rp) — kosongkan untuk "Hubungi via WhatsApp"</Label>
              <Input type="number" value={form.price} onChange={(e) => set("price")(e.target.value)} data-testid="form-price" placeholder="cth: 15999000" className="mt-1.5 border-white/10 bg-white/5 text-white" />
            </div>
            <div>
              <Label className="text-gray-300">Gambar Produk</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                hidden
                data-testid="form-file-input"
                onChange={(e) => handleUpload(e.target.files)}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="form-upload-button"
                className="mt-1.5 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 bg-white/5 px-4 py-4 text-sm font-medium text-gray-300 transition-colors hover:border-berry hover:text-white disabled:opacity-60"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Mengunggah..." : "Unggah Foto dari Perangkat"}
              </button>

              {imageList().length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-2" data-testid="form-image-previews">
                  {imageList().map((src, i) => (
                    <div key={`${src}-${i}`} className="group relative aspect-square overflow-hidden rounded-lg border border-white/10 bg-[#0B0C10]">
                      <img src={src} alt={`preview-${i}`} className="h-full w-full object-contain p-1" />
                      {i === 0 && (
                        <span className="absolute left-1 top-1 rounded bg-berry px-1 text-[9px] font-semibold text-white">Utama</span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        data-testid={`remove-image-${i}`}
                        className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <Textarea
                value={form.images}
                onChange={(e) => set("images")(e.target.value)}
                data-testid="form-image"
                rows={2}
                placeholder={"Atau tempel URL gambar (satu per baris)"}
                className="mt-3 border-white/10 bg-white/5 text-xs text-gray-400"
              />
              <p className="mt-1 text-xs text-gray-500">Unggah foto resmi produk, atau tempel URL. Foto pertama menjadi gambar utama.</p>
            </div>
            <div>
              <Label className="text-gray-300">Deskripsi</Label>
              <Textarea value={form.description} onChange={(e) => set("description")(e.target.value)} data-testid="form-description" rows={2} className="mt-1.5 border-white/10 bg-white/5 text-white" />
            </div>
            <div>
              <Label className="text-gray-300">Spesifikasi (satu per baris)</Label>
              <Textarea value={form.specs} onChange={(e) => set("specs")(e.target.value)} data-testid="form-specs" rows={3} placeholder={"Layar 6.7 inch\nBaterai 5000mAh"} className="mt-1.5 border-white/10 bg-white/5 text-white" />
            </div>
            <div className="flex flex-wrap gap-6 pt-1">
              <div className="flex items-center gap-2"><Switch checked={form.in_stock} onCheckedChange={set("in_stock")} data-testid="form-instock" /><Label className="text-gray-300">Tersedia</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.featured} onCheckedChange={set("featured")} data-testid="form-featured" /><Label className="text-gray-300">Unggulan</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.bestseller} onCheckedChange={set("bestseller")} data-testid="form-bestseller" /><Label className="text-gray-300">Best Seller</Label></div>
            </div>
            <DialogFooter>
              <button type="submit" disabled={saving} data-testid="form-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-berry px-6 py-2.5 font-semibold text-white transition-colors hover:bg-berry-dark disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Simpan Perubahan" : "Tambah Produk"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-[#12141C] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus produk?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Produk "{deleteTarget?.name}" akan dihapus permanen dari katalog.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-white hover:bg-white/5">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete" className="bg-red-600 text-white hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
